import axios from 'axios';
import { CognitoUserPool } from 'amazon-cognito-identity-js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://myhomeentry.com/api';

// Track redirect to prevent infinite loops
let redirectInProgress = false;
let last401Timestamp = 0;
const REDIRECT_DEBOUNCE_TIME = 5000; // 5 seconds

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get the current Cognito ID token (for use outside React components)
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
};
const userPool = new CognitoUserPool(poolData);
const getCognitoIdToken = () => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession((err, session) => {
        if (err || !session || !session.isValid()) {
          reject('No valid session');
        } else {
          const idToken = session.getIdToken().getJwtToken();
          resolve(idToken);
        }
      });
    } else {
      reject('No user');
    }
  });
};

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    try {
      // First try to get token from localStorage (MSG91 auth)
      let token = localStorage.getItem('idToken') || localStorage.getItem('accessToken');
      
      // If no localStorage token, try Cognito session (fallback for legacy auth)
      if (!token) {
        token = await getCognitoIdToken();
      }
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        config.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // console.log('[API] No valid session found:', error); // Removed
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Prevent infinite loop - check if we're already handling a 401 or on login page
      const currentPath = window.location.pathname;
      const isOnLoginPage = currentPath === '/login' || currentPath === '/mobile-login' || currentPath === '/';
      const now = Date.now();
      
      // Debounce multiple 401s within 5 seconds
      if (redirectInProgress || (now - last401Timestamp < REDIRECT_DEBOUNCE_TIME)) {
        console.warn('[API] 401 redirect already in progress or too recent, skipping');
        return Promise.reject(error);
      }
      
      // If already on login page, don't redirect - just clear tokens
      if (isOnLoginPage) {
        console.warn('[API] 401 on login page, clearing tokens without redirect');
        clearAuthenticationData();
        return Promise.reject(error);
      }
      
      // Mark redirect in progress and update timestamp
      redirectInProgress = true;
      last401Timestamp = now;
      
      try {
        // Clear all authentication data
        await clearAuthenticationData();
        
        console.log('[API] 401 detected, redirecting to login');
        // Use setTimeout to ensure cleanup completes before redirect
        setTimeout(() => {
          redirectInProgress = false;
          window.location.href = '/login';
        }, 100);
      } catch (signOutError) {
        console.error('[API] Error during 401 cleanup:', signOutError);
        redirectInProgress = false;
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to clear all authentication data
const clearAuthenticationData = async () => {
  try {
    // Clear localStorage tokens
    const tokenKeys = ['idToken', 'accessToken', 'refreshToken', 'tokenExpiry', 'user'];
    tokenKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove ${key} from localStorage:`, e);
      }
    });
    
    // Clear Cognito session
    try {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        await new Promise((resolve) => {
          cognitoUser.signOut();
          resolve();
        });
      }
    } catch (cognitoError) {
      console.warn('Failed to clear Cognito session:', cognitoError);
    }
    
    console.log('[API] Authentication data cleared successfully');
  } catch (error) {
    console.error('[API] Error clearing authentication data:', error);
  }
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getUsers: (buildingId) => api.get(`/users/building/${buildingId}`),
  getAssociations: (userId) => api.get(`/users/${userId}/associations`),
  addAssociations: (userId, data) => api.post(`/users/${userId}/associations`, data),
  removeAssociations: (userId, data) => api.delete(`/users/${userId}/associations`, { data }),
};

// Buildings API
export const buildingsAPI = {
  getAll: () => api.get('/buildings'),
  getById: (id) => api.get(`/buildings/${id}`),
  create: (data) => api.post('/buildings', data),
  update: (id, data) => api.put(`/buildings/${id}`, data),
  delete: (id) => api.delete(`/buildings/${id}`),
};

// Flats API
export const flatsAPI = {
  getAll: () => api.get('/flats'),
  getById: (id) => api.get(`/flats/${id}`),
  create: (data) => api.post('/flats', data),
  update: (id, data) => api.put(`/flats/${id}`, data),
  delete: (id) => api.delete(`/flats/${id}`),
  getByBuilding: (buildingId) => api.get(`/flats/building/${buildingId}`),
  getDetails: (flatId, flatNumber) => api.get(`/flats/details/${flatId}`, { params: { flatNumber } }),
  getHistory: (flatId) => api.get(`/flats/history`, { params: { flatId } }),
  // QR Code validation for vehicle entry
  validateQRCode: (vehicleNumber, buildingId = null) => {
    const endpoint = buildingId 
      ? `/flats/building/${buildingId}` 
      : `/flats/building/DUMMY_BUILDING`;
    return api.get(endpoint, { params: { qrCode: vehicleNumber } });
  },
  // Vehicle entry logs
  getVehicleEntryLogs: (params = {}) => {
    return api.get('/flats/vehicle-entry-logs', { params });
  },
  // Create vehicle entry log
  createVehicleEntryLog: (data) => {
    return api.post('/flats/vehicle-entry-logs', data);
  },
};

// Payments API
export const paymentsAPI = {
  getAll: () => api.get('/payments'),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  pay: (paymentId) => api.post(`/payments/${paymentId}/pay`),
  // New retrieval methods based on access patterns
  getByBuilding: (buildingId) => api.get('/payments', { params: { buildingId } }),
  getByBuildingAndFlat: (buildingId, flatId) => api.get('/payments', { params: { buildingId, flatId } }),
  getByBuildingYearMonth: (buildingId, year, month) => api.get('/payments', { params: { buildingId, year, month } }),
  getByBuildingFlatYearMonth: (buildingId, flatId, year, month) => api.get('/payments', { params: { buildingId, flatId, year, month } }),
  getByFlat: (flatId) => api.get(`/payments/flat/${flatId}`),
  
  // CCAvenue payment methods
  initiateCCAvPayment: (data) => api.post('/ccavenue/initiate', data),
  payWithCCAvenue: (data) => api.put(`/payments/${data.paymentId}`, data),
  
  // Razorpay methods
  createRazorpayOrder: (data) => api.post('/razorpay-order', data),
  payWithRazorpay: (data) => api.put(`/payments/${data.paymentId}`, data),
  
  // Building summary method
  getBuildingSummary: (buildingId) => api.get('/payments', { params: { buildingId, summary: 'true' } }),
};

// Complaints API
export const complaintsAPI = {
  getAll: () => api.get('/complaints'),
  getById: (id) => api.get(`/complaints/${id}`),
  create: (data) => api.post('/complaints', data),
  update: (id, data) => api.put(`/complaints/${id}`, data),
  delete: (id) => api.delete(`/complaints/${id}`),
  getByBuilding: (buildingId) => api.get(`/complaints/building/${buildingId}`),
  getByFlat: (flatId) => api.get(`/complaints/flat/${flatId}`)
};

// Maintenance Staff API
export const maintenanceStaffAPI = {
  getAll: () => api.get('/maintenance-staff'),
  getById: (id) => api.get(`/maintenance-staff/${id}`),
  create: (data) => api.post('/maintenance-staff', data),
  update: (id, data) => api.put(`/maintenance-staff/${id}`, data),
  delete: (id) => api.delete(`/maintenance-staff/${id}`),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getById: (id) => api.get(`/notifications/${id}`),
  create: (data) => api.post('/notifications', data),
  update: (id, data) => api.put(`/notifications/${id}`, data),
  delete: (id) => api.delete(`/notifications/${id}`),
  
  // Mass notification endpoints
  sendMassNotification: (data) => api.post('/notifications/mass', data),
  getTemplates: () => api.get('/notifications/templates'),
  
  // User-specific notifications
  getUserNotifications: (userId) => api.get(`/notifications/user/${userId}`),
  markAsRead: (notificationId) => api.put(`/notifications/read/${notificationId}`, {}),
};

// Visitor Management API
export const visitorsAPI = {
  // Get all visitors for a building
  getByBuilding: (buildingId) => api.get(`/visitors/building/${buildingId}`),
  // Get all visitors for a flat
  getByFlat: (flatId) => api.get(`/visitors/flat/${flatId}`),
  // Create a new visitor request
  create: (data) => api.post('/visitors', data),
  // Update a visitor request (status or details)
  update: (buildingId, visitorId, data) =>
    api.put(`/visitors/building/${buildingId}/visitor/${visitorId}`, data),
  getByDate: (dateStr) => api.get(`/visitors?date=${dateStr}`),
  getByBuildingAndDate: (buildingId, dateStr) => api.get(`/visitors/building/${buildingId}`, { params: { date: dateStr } }), // <-- Add this line
};

// Facilities API
export const facilitiesAPI = {
  getAll: (buildingId) => api.get('/facilities', { params: { buildingId } }),
  create: (data) => api.post('/facilities', data),
  update: (data) => api.put('/facilities', data),
};

// Facility Bookings API
export const facilityBookingsAPI = {
  create: (data) => api.post('/facility-bookings', data),
  update: (data) => api.put('/facility-bookings', data),
  delete: (bookingId) => api.delete('/facility-bookings', { params: { bookingId } }),
  getByUser: (userId) => api.get('/facility-bookings', { params: { userId } }),
  getByBuilding: (buildingId) => api.get('/facility-bookings', { params: { buildingId } }),
  getByDateRange: (buildingId, startDate, endDate) => api.get('/facility-bookings', { params: { buildingId, startDate, endDate } }),
  getByFacilityAndDate: (facilityId, buildingId, year, month) => api.get('/facility-bookings', { params: { facilityId, buildingId, year, month } }),
  getByFacilityDate: (facilityId, buildingId, date) => api.get('/facility-bookings', { params: { facilityId, buildingId, date } }),
  getByBookingId: (bookingId) => api.get('/facility-bookings', { params: { bookingId } }),
};

// Building Expenses API
export const expensesAPI = {
  // Get all expenses with optional filters
  getAll: (buildingId, filters = {}) => api.get('/expenses', { params: { buildingId, ...filters } }),
  
  // Get specific expense by ID
  getById: (expenseId, buildingId) => api.get(`/expenses/${expenseId}`, { params: { buildingId } }),
  
  // Create new expense
  create: (buildingId, data) => api.post('/expenses', data, { params: { buildingId } }),
  
  // Update existing expense
  update: (expenseId, buildingId, data) => api.put(`/expenses/${expenseId}`, data, { params: { buildingId } }),
  
  // Delete expense (soft delete)
  delete: (expenseId, buildingId) => api.delete(`/expenses/${expenseId}`, { params: { buildingId } }),
  
  // Approve or reject expense
  approve: (expenseId, buildingId, approvalData) => 
    api.post(`/expenses/${expenseId}/approve`, approvalData, { params: { buildingId } }),
  
  // Get monthly summary
  getMonthlySummary: (buildingId, month) => api.get(`/expenses/summary/${buildingId}/${month}`),
  
  // Get expense categories and metadata
  getCategories: () => api.get('/expenses/categories'),
  
  // Upload receipt file
  uploadReceipt: (buildingId, expenseId, formData) => 
    api.post('/expenses/receipts/upload', formData, {
      params: { buildingId, expenseId },
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  // Get expenses by category
  getByCategory: (buildingId, category, filters = {}) => 
    api.get('/expenses', { params: { buildingId, category, ...filters } }),
  
  // Get expenses by month
  getByMonth: (buildingId, month, filters = {}) => 
    api.get('/expenses', { params: { buildingId, month, ...filters } }),
  
  // Get expenses by vendor
  getByVendor: (buildingId, vendorId, filters = {}) => 
    api.get('/expenses', { params: { buildingId, vendorId, ...filters } }),
  
  // Get expenses by logged user
  getByLoggedUser: (buildingId, loggedBy, filters = {}) => 
    api.get('/expenses', { params: { buildingId, loggedBy, ...filters } }),
  
  // Get expenses by approval status
  getByApprovalStatus: (buildingId, approvalStatus, filters = {}) => 
    api.get('/expenses', { params: { buildingId, approvalStatus, ...filters } }),
  
  // Get expenses by urgency level
  getByUrgency: (buildingId, urgencyLevel, filters = {}) => 
    api.get('/expenses', { params: { buildingId, urgencyLevel, ...filters } }),
  
  // Get expenses within amount range
  getByAmountRange: (buildingId, minAmount, maxAmount, filters = {}) => 
    api.get('/expenses', { params: { buildingId, minAmount, maxAmount, ...filters } }),
  
  // Generate expense report (if implemented)
  generateReport: (buildingId, reportType, params = {}) => 
    api.get('/expenses/reports', { params: { buildingId, reportType, ...params } }),
  
  // Get budget analysis (if implemented)
  getBudgetAnalysis: (buildingId, month) => 
    api.get('/expenses/budget-analysis', { params: { buildingId, month } })
};

// Utility function for making API calls with proper error handling
export const apiCall = async (endpoint, method = 'GET', data = null, config = {}) => {
  try {
    const response = await api({
      url: endpoint,
      method,
      data,
      ...config
    });
    
    return {
      success: true,
      data: response.data,
      status: response.status
    };
  } catch (error) {
    console.error(`API call failed: ${method} ${endpoint}`, error);
    
    return {
      success: false,
      error: error.response?.data || error.message,
      status: error.response?.status || 500
    };
  }
};

export default api;